import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { drawerEnter, drawerLeave } from '@animations/modal-drawer';
import { CustomersService } from '@features/customers/admin/services/customers.service';
import { environment } from '@environments/environment';
import { CountriesService } from '@features/countries/services/countries.service';
import { LeadsService } from '@features/leads/services/leads.service';
import { UserSelectorContainerComponent } from '@features/users/shared/containers/user-selector-container/user-selector-container.component';
import { AlertController, ModalController } from '@ionic/angular';
import { regexList } from '@shared/constants/regex-list';
import { LeadCustomerSelectorComponent } from '../lead-customer-selector/lead-customer-selector.component';
import { Country } from '@shared/models/country';
import { Lead } from '@shared/models/lead';
import { Group } from '@shared/models/group';
import { Contact } from '@shared/models/contact';
import { Observable, take } from 'rxjs';
import { LeadSourceSelectorComponent } from '@features/leads/components/lead-source-selector/lead-source-selector.component';
import { PopoverService } from '@shared/services/popover.service';

@Component({
  selector: 'app-add-edit-lead',
  templateUrl: './add-edit-lead.component.html',
  styleUrls: ['./add-edit-lead.component.scss'],
  standalone: false,
})
export class AddEditLeadComponent implements OnInit {
  @Input() lead: Lead;

  countries$: Observable<Country[]>;
  selectedCountry$: Observable<Country | undefined>;
  isEdit: boolean;
  leadDetailsForm: FormGroup;
  leadCustomerDetailsForm: FormGroup;
  ownerName: string | undefined;

  constructor(
    private modalController: ModalController,
    private leadsService: LeadsService,
    private customersService: CustomersService,
    private countriesService: CountriesService,
    private router: Router,
    private popoverService: PopoverService,
    private alertController: AlertController,
  ) {}

  ngOnInit(): void {
    this.isEdit = !!this.lead;
    this.countries$ = this.countriesService.getCountriesByTradingEntityAndGroupType(
      this.lead?.attributes.trading_entity || environment.trading_entity,
      'customer',
    );
    this.selectedCountry$ = this.countriesService.getSelectedCountry();
    this.ownerName = this.lead?.attributes.owner_name || undefined;
    this.setFormData();
    this.countriesService.selectCountry(this.leadCustomerDetailsForm.controls['country_alpha2_code'].value);

    this.setCompanyRegistrationNumberValidators(this.leadCustomerDetailsForm.controls['group_entity_type'].value);

    this.leadCustomerDetailsForm.controls['group_entity_type'].valueChanges.subscribe((value) => {
      this.setCompanyRegistrationNumberValidators(value);
    });

    this.leadCustomerDetailsForm.controls['country_alpha2_code'].valueChanges.subscribe((value) => {
      this.countriesService.selectCountry(value);
      this.setVatNumberValidators(value);
    });

    if (this.leadCustomerDetailsForm.controls['group_id'].value && this.lead.attributes.group) {
      this.setLeadCustomerFieldsFromGroup(this.lead.attributes.group);
    }
  }

  setFormData() {
    this.leadDetailsForm = new FormGroup({
      lead_source: new FormControl(this.lead?.attributes.lead_source || undefined, [Validators.required]),
      lead_source_tag: new FormControl(this.lead?.attributes.lead_source_tag || undefined),
      description: new FormControl(this.lead?.attributes.description || undefined),
      quantity: new FormControl(this.lead?.attributes.quantity || undefined),
      owner_id: new FormControl(this.lead?.attributes.owner_id || undefined),
    });

    this.leadCustomerDetailsForm = new FormGroup({
      group_id: new FormControl(this.lead?.attributes.group_id || undefined),
      group_entity_type: new FormControl(this.lead?.attributes.group_entity_type || 'company', [Validators.required]),
      parent_group_id: new FormControl(this.lead?.attributes.parent_group_id || undefined),
      parent_group_name: new FormControl(this.lead?.attributes.parent_group_name || undefined),
      customer_name: new FormControl(this.lead?.attributes.customer_name, {
        validators: [Validators.required, Validators.pattern(regexList.group_name), Validators.maxLength(100)],
      }),
      contact_name: new FormControl(this.lead?.attributes.contact_name || undefined),
      contact_email: new FormControl(this.lead?.attributes.contact_email, [Validators.required, Validators.email]),
      contact_telephone_number: new FormControl(this.lead?.attributes.contact_telephone_number || undefined),
      company_registration_number: new FormControl(this.lead?.attributes.company_registration_number || undefined, [
        Validators.pattern(/^(?!.*not available).*/i),
        Validators.minLength(6),
        Validators.maxLength(100),
      ]),
      vat_number: new FormControl(this.lead?.attributes.vat_number || undefined, {
        validators: [
          Validators.minLength(this.lead?.attributes.country_alpha2_code === 'ZA' ? 9 : 1),
          Validators.maxLength(this.lead?.attributes.country_alpha2_code === 'ZA' ? 10 : 99),
        ],
        updateOn: 'blur',
      }),
      country_alpha2_code: new FormControl(
        this.lead?.attributes.country_alpha2_code || environment.default_country,
        Validators.required,
      ),
    });
  }

  setCompanyRegistrationNumberValidators(groupEntityType: string) {
    if (groupEntityType === 'company') {
      this.leadCustomerDetailsForm.controls['company_registration_number'].setValidators([
        Validators.pattern(/^(?!.*not available).*/i),
        Validators.minLength(6),
        Validators.maxLength(100),
      ]);
    } else {
      this.leadCustomerDetailsForm.controls['company_registration_number'].clearValidators();
      this.leadCustomerDetailsForm.controls['company_registration_number'].setValue('');
    }
    this.leadCustomerDetailsForm.controls['company_registration_number'].updateValueAndValidity();
  }

  setVatNumberValidators(countryAlpha2Code: string) {
    this.leadCustomerDetailsForm.controls['vat_number'].setValidators([
      Validators.minLength(countryAlpha2Code === 'ZA' ? 9 : 1),
      Validators.maxLength(countryAlpha2Code === 'ZA' ? 10 : 99),
    ]);
    this.leadCustomerDetailsForm.controls['vat_number'].updateValueAndValidity();
  }

  async presentOwnerSelectorModal() {
    const modal = await this.modalController.create({
      component: UserSelectorContainerComponent,
      componentProps: { showRemoveOwner: !!this.leadDetailsForm.controls['owner_id'].value },
      cssClass: 'drawer-style-sm layered-modal',
      enterAnimation: drawerEnter,
      leaveAnimation: drawerLeave,
    });
    modal.onDidDismiss().then((result) => {
      if (result.data?.removeOwner) {
        this.leadDetailsForm.patchValue({ owner_id: null });
        this.ownerName = undefined;
        this.leadDetailsForm.controls['owner_id'].markAsDirty();
      } else if (result.data?.user) {
        this.leadDetailsForm.patchValue({ owner_id: result.data.user.id });
        this.ownerName = result.data.user.attributes.full_name;
        this.leadDetailsForm.controls['owner_id'].markAsDirty();
      }
    });
    return await modal.present();
  }

  async presentLeadSourceTagPopover(event: Event): Promise<void> {
    const popover = await this.popoverService.create({
      component: LeadSourceSelectorComponent,
      componentProps: {
        leadSource: this.leadDetailsForm.controls['lead_source'].value,
        leadSourceTag: this.leadDetailsForm.controls['lead_source_tag'].value,
      },
      event: event,
      translucent: true,
      dismissOnSelect: false,
      keepContentsMounted: false,
      mode: 'md',
      size: 'auto',
      alignment: 'start',
      cssClass: 'lead-source-tag-popover-shell [--max-width:calc(100vw_-_1rem)] [--width:370px]',
    });

    popover.onDidDismiss().then((data) => {
      if (data?.data?.leadSource) {
        this.leadDetailsForm.controls['lead_source'].setValue(data.data.leadSource);
        this.leadDetailsForm.controls['lead_source_tag'].setValue(data.data.leadSourceTag);
      }
      this.leadDetailsForm.controls['lead_source'].markAsDirty();
      this.leadDetailsForm.controls['lead_source'].markAsTouched();
      this.leadDetailsForm.controls['lead_source'].updateValueAndValidity();
      this.leadDetailsForm.controls['lead_source_tag'].markAsDirty();
      this.leadDetailsForm.controls['lead_source_tag'].updateValueAndValidity();
    });

    return await popover.present();
  }

  async presentLeadCustomerSelectorModal() {
    if (this.leadDetailsForm.dirty || this.leadCustomerDetailsForm.dirty) {
      await this.presentDiscardChangesBeforeCreateCustomer();
      return;
    }
    const modal = await this.modalController.create({
      component: LeadCustomerSelectorComponent,
      id: 'lead-customer-selector-modal',
      componentProps: { lead: this.lead },
      cssClass: 'drawer-style-md layered-modal',
      enterAnimation: drawerEnter,
      leaveAnimation: drawerLeave,
    });
    modal.onDidDismiss().then((result) => {
      if (result.data?.group) {
        const selectedGroup = result.data.group as Group;
        if (selectedGroup.id) {
          this.leadsService.updateLead({
            id: this.lead.id,
            attributes: { group_id: selectedGroup.id },
          } as Partial<Lead>);
        }
        this.closeModal();
      }
    });
    return await modal.present();
  }

  async presentDiscardChangesBeforeCreateCustomer(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Discard lead changes?',
      message: 'Continue to discard your changes and create a customer.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          id: 'cancel-discard-changes-before-create-customer',
        },
        {
          text: 'Continue',
          role: 'confirm',
          id: 'confirm-discard-changes-before-create-customer',
          handler: () => {
            this.setFormData();
            this.presentLeadCustomerSelectorModal();
          },
        },
      ],
    });
    await alert.present();
  }

  async presentUnlinkCustomerConfirmation(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Unlink customer?',
      message: 'This will remove the customer from the lead and restore the captured lead information.',
      buttons: [
        { text: 'Cancel', role: 'cancel', id: 'cancel-unlink-customer' },
        {
          text: 'Unlink customer',
          role: 'confirm',
          id: 'confirm-unlink-customer',
          handler: () => {
            this.leadsService.updateLead({
              id: this.lead.id,
              attributes: { group_id: null },
            } as Partial<Lead>);

            this.closeModal();
          },
        },
      ],
    });
    await alert.present();
  }

  setLeadCustomerFieldsFromGroup(group: Group): void {
    const primaryContact =
      group.attributes.contacts.find((contact) => contact.contact_roles?.includes('primary')) ??
      group.attributes.contacts[0];

    this.leadCustomerDetailsForm.patchValue({
      group_entity_type: group.attributes.group_entity_type,
      customer_name: group.attributes.name,
      contact_name: primaryContact?.name,
      contact_email: primaryContact?.email,
      contact_telephone_number: primaryContact?.telephone_number || group.attributes.company_telephone_number,
      contact_telephone_dial_code: primaryContact?.telephone_dial_code,
      company_registration_number: group.attributes.company_registration_number,
      vat_number: group.attributes.vat_number,
      country_alpha2_code: group.attributes.country_alpha2_code,
    });
  }

  selectParent(parentGroup: Group): void {
    this.leadCustomerDetailsForm.patchValue({
      parent_group_id: parentGroup?.id || null,
      parent_group_name: parentGroup?.attributes.name,
    });
    this.leadCustomerDetailsForm.controls['parent_group_id'].markAsDirty();
  }

  navigateToFullEditCustomerDetails() {
    this.closeModal();
    this.router.navigateByUrl('customers/edit/' + this.leadCustomerDetailsForm.controls['group_id'].value);
  }

  closeModal() {
    this.modalController.dismiss({});
  }

  saveLead() {
    this.leadDetailsForm.markAllAsTouched();
    this.leadCustomerDetailsForm.markAllAsTouched();
    this.leadDetailsForm.updateValueAndValidity();
    this.leadCustomerDetailsForm.updateValueAndValidity();
    if (this.leadDetailsForm.invalid || this.leadCustomerDetailsForm.invalid) return;

    const hasAssignedCustomer = !!this.leadCustomerDetailsForm.controls['group_id'].value;

    const leadAttributes: Partial<Lead['attributes']> = Object.keys(this.leadDetailsForm.controls)
      .filter((key) => this.leadDetailsForm.get(key)?.dirty)
      .reduce((attrs, key) => ({ ...attrs, [key]: this.leadDetailsForm.value[key] }), {});

    const leadCustomerAttributes: Partial<Lead['attributes']> = Object.keys(this.leadCustomerDetailsForm.controls)
      .filter((key) => this.leadCustomerDetailsForm.get(key)?.dirty)
      .reduce((attrs, key) => ({ ...attrs, [key]: this.leadCustomerDetailsForm.value[key] }), {});

    this.selectedCountry$.pipe(take(1)).subscribe((selectedCountry) => {
      if (
        this.leadCustomerDetailsForm.controls['contact_telephone_number'].dirty ||
        this.leadCustomerDetailsForm.controls['country_alpha2_code'].dirty
      ) {
        leadCustomerAttributes.contact_telephone_dial_code = this.leadCustomerDetailsForm.value.contact_telephone_number
          ? String(selectedCountry?.attributes.dial_code)
          : undefined;
      }
    });

    if (
      this.leadCustomerDetailsForm.controls['customer_name'].dirty &&
      leadCustomerAttributes.group_entity_type === 'person'
    ) {
      leadCustomerAttributes.contact_name = leadCustomerAttributes.customer_name;
    }

    if (!this.isEdit) {
      this.leadsService.createLead({
        attributes: {
          ...leadAttributes,
          ...leadCustomerAttributes,
          group_entity_type: this.leadCustomerDetailsForm.controls['group_entity_type'].value,
          country_alpha2_code: this.leadCustomerDetailsForm.controls['country_alpha2_code'].value,
        },
      } as Partial<Lead>);
    } else {
      if (!hasAssignedCustomer) {
        this.leadsService.updateLead({
          id: this.lead?.id,
          attributes: {
            ...leadAttributes,
            ...leadCustomerAttributes,
          },
        } as Partial<Lead>);
      } else {
        if (Object.keys(leadAttributes).length > 0) {
          this.leadsService.updateLead({
            id: this.lead?.id,
            attributes: { ...leadAttributes, group_id: this.leadCustomerDetailsForm.controls['group_id'].value },
          } as Partial<Lead>);
        }
        if (Object.keys(leadCustomerAttributes).length > 0) {
          this.updateAssignedCustomer(leadCustomerAttributes);
        }
      }
    }
    this.closeModal();
  }

  updateAssignedCustomer(leadCustomerAttributes: Partial<Lead['attributes']>): void {
    const groupContacts = this.lead.attributes.group?.attributes.contacts;

    const updatedPrimaryContact: Contact = {
      ...groupContacts?.find((contact) => contact.contact_roles?.includes('primary')),
      name:
        this.leadCustomerDetailsForm.controls['group_entity_type'].value === 'person'
          ? this.leadCustomerDetailsForm.controls['customer_name'].value
          : this.leadCustomerDetailsForm.controls['contact_name'].value,
      email: this.leadCustomerDetailsForm.controls['contact_email'].value,
      telephone_number: this.leadCustomerDetailsForm.controls['contact_telephone_number'].value,
      telephone_dial_code: this.leadCustomerDetailsForm.controls['contact_telephone_number'].value
        ? leadCustomerAttributes.contact_telephone_dial_code
        : undefined,
      contact_roles: ['primary'],
    } as Contact;

    const contacts = groupContacts?.map((contact) =>
      contact.contact_roles?.includes('primary') ? updatedPrimaryContact : contact,
    );

    const groupAttributes: Partial<Group['attributes']> = {
      group_entity_type: leadCustomerAttributes.group_entity_type,
      name: leadCustomerAttributes.customer_name,
      company_registration_number: leadCustomerAttributes.company_registration_number,
      vat_number: leadCustomerAttributes.vat_number,
      country_alpha2_code: leadCustomerAttributes.country_alpha2_code,
      contacts: contacts,
    } as Partial<Group['attributes']>;

    this.customersService.updateCustomer({
      id: this.leadCustomerDetailsForm.controls['group_id'].value,
      attributes: groupAttributes,
    } as Partial<Group>);
  }
}
